import React from 'react';
import { Trophy, Target, Users, Play, CheckCircle, ArrowRight } from 'lucide-react';

export function QuickStart() {
  const steps = [
    {
      icon: Trophy,
      title: "Create Tournament",
      description: "Click 'New Tournament' to start the setup wizard",
      details: "Choose tournament name, dates, and format (Singles/Fourball/Foursomes)"
    },
    {
      icon: Users,
      title: "Add Players",
      description: "Add players and assign teams (for team formats)",
      details: "Enter player names, handicaps, and team assignments"
    },
    {
      icon: Target,
      title: "Generate Matches",
      description: "Review and generate match pairings",
      details: "Preview matches and start the tournament"
    },
    {
      icon: Play,
      title: "Live Scoring",
      description: "Score matches hole-by-hole in real-time",
      details: "Format-specific scoring with automatic calculations"
    },
    {
      icon: CheckCircle,
      title: "Live Results",
      description: "View real-time leaderboards and match status",
      details: "Track tournament progress and match results"
    }
  ];

  const features = [
    "✅ **Singles Format**: Head-to-head individual matches",
    "✅ **Four Ball**: Best ball team format with individual scoring", 
    "✅ **Foursomes**: Alternate shot team format",
    "✅ **Real-time Updates**: Live scoring across all devices",
    "✅ **Professional UI**: Tournament-grade interface",
    "✅ **Smart Calculations**: Automatic handicap and match status"
  ];

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="p-3 bg-primary-100 rounded-xl">
            <Trophy className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Matchplay v2 System</h1>
        </div>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Professional-grade matchplay tournament management with real-time scoring, 
          live leaderboards, and support for Singles, Four Ball, and Foursomes formats.
        </p>
      </div>

      {/* Quick Start Steps */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
          🚀 Quick Start Guide
        </h2>
        
        <div className="space-y-6">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={index} className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary-100 text-primary-600 rounded-full">
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="bg-primary-600 text-white text-sm font-medium px-2 py-1 rounded">
                      Step {index + 1}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-gray-700 mb-1">{step.description}</p>
                  <p className="text-sm text-gray-500">{step.details}</p>
                </div>
                {index < steps.length - 1 && (
                  <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-2" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Features */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 text-center">
          ⭐ System Features
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {features.map((feature, index) => (
            <div key={index} className="text-gray-700">
              {feature}
            </div>
          ))}
        </div>
      </div>

      {/* Testing Notes */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-yellow-900 mb-3">
          🧪 Testing Notes
        </h3>
        <ul className="space-y-2 text-yellow-800">
          <li>• <strong>Database Ready:</strong> All Supabase tables are configured and ready</li>
          <li>• <strong>Real-time:</strong> Scores sync instantly across devices</li>
          <li>• <strong>Mobile Optimized:</strong> Perfect for on-course scoring</li>
          <li>• <strong>Format Support:</strong> All three matchplay formats implemented</li>
          <li>• <strong>Production Ready:</strong> Error handling and validation included</li>
        </ul>
      </div>

      {/* System Status */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-green-900 mb-3">
          ✅ System Status
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-4 h-4" />
              <span>Database Schema: Ready</span>
            </div>
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-4 h-4" />
              <span>Real-time Subscriptions: Active</span>
            </div>
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-4 h-4" />
              <span>Tournament Setup: Complete</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-4 h-4" />
              <span>Scoring System: Complete</span>
            </div>
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-4 h-4" />
              <span>Leaderboard: Complete</span>
            </div>
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-4 h-4" />
              <span>Integration: Ready</span>
            </div>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="text-center">
        <div className="bg-primary-600 text-white rounded-xl p-6">
          <h3 className="text-xl font-semibold mb-3">🎯 Ready to Test!</h3>
          <p className="mb-4">
            Your complete matchplay system is ready for testing. Click the 
            <strong> "New Tournament"</strong> button above to create your first tournament.
          </p>
          <div className="text-sm opacity-90">
            The system supports real-time scoring, live leaderboards, and all major matchplay formats.
            Perfect for testing with multiple devices to see real-time updates in action!
          </div>
        </div>
      </div>
    </div>
  );
} 